/**
 * The listing's arithmetic, kept pure so it can be tested and shared by the cards, the unit
 * page's decide sheet and the Brand settings preview. Dates are calendar days in local time:
 * `new Date('2026-11-01')` is UTC midnight and would read as Oct 31 in the US.
 */

export interface PublicListingUnit {
  /** The key of the home's page (`/homes/{publicUnitKey}`): the listing's public id on a storefront. */
  publicUnitKey?: string;
  /** The key of the hosted apply flow (`/apply/{applyKey}`) when it differs from the page key. */
  applyKey?: string;
  unitId?: string;
  propertyId?: string;
  unitName?: string;
  propertyName?: string;
  bedrooms?: number;
  bathrooms?: number;
  areaSqft?: number;
  rent?: number;
  availableFrom?: string | null;
  applyEligible?: boolean;
  petFriendly?: boolean;
  leaseTermMonths?: number;
  coverImageUrl?: string;
  city?: string;
  state?: string;
  latitude?: number;
  longitude?: number;
  photoUrls?: string[];
  address1?: string;
  address2?: string;
  postalCode?: string;
  country?: string;
  description?: string;
  amenities?: string[];
  virtualTourUrl?: string;
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/;
const DAY_MS = 86_400_000;

/** Reads `YYYY-MM-DD` (or the date part of an ISO timestamp) as a local calendar day. */
export function parseIsoDate(value: string | null | undefined): Date | null {
  const match = value?.match(ISO_DATE);
  if (!match) return null;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ? date : null;
}

export function toIsoDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function daysBetween(from: Date, to: Date): number {
  return Math.round((startOfDay(to).getTime() - startOfDay(from).getTime()) / DAY_MS);
}

/** "Nov 1", with the year only when it is not this year. */
export function formatShortDate(date: Date, today: Date = new Date()): string {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(date.getFullYear() === today.getFullYear() ? {} : { year: 'numeric' }),
  });
}

export function formatLongDate(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatRent(value: number | null | undefined): string | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
}

/**
 * The gap between two days in words: "3 days", "2 weeks", "a month", "5 weeks", "3 months".
 * An exact calendar month (Nov 1 → Dec 1) reads as months; otherwise days under two weeks,
 * weeks under nine weeks, then months.
 */
export function describeGap(from: Date, to: Date): string {
  const days = Math.abs(daysBetween(from, to));
  const [early, late] = from <= to ? [from, to] : [to, from];
  const months = (late.getFullYear() - early.getFullYear()) * 12 + late.getMonth() - early.getMonth();
  if (months > 0 && late.getDate() === early.getDate()) return months === 1 ? 'a month' : `${months} months`;
  if (days < 14) return days === 1 ? '1 day' : `${days} days`;
  if (days < 63) return `${Math.round(days / 7)} weeks`;
  const roughMonths = Math.round(days / 30.44);
  return roughMonths <= 1 ? 'a month' : `${roughMonths} months`;
}

export type DateFitKind = 'closed' | 'ready' | 'later';

export interface DateFit {
  kind: DateFitKind;
  /** The chip's lead text: "Ready for Nov 1", "Nov 15", "Ready now", "Available Dec 1". */
  label: string;
  /** The chip's quiet tail: "2 weeks later". */
  detail: string | null;
  tone: 'ok' | 'later' | 'none';
  /** When the home is free; null means now. */
  availableOn: Date | null;
}

/**
 * How a home's availability meets the renter's move-in date. A home that is not taking
 * applications says so and sorts last; a null `availableFrom` means "now" only for homes that are.
 */
export function dateFit(unit: Pick<PublicListingUnit, 'availableFrom' | 'applyEligible'>, moveIn: Date | null, today: Date): DateFit {
  if (unit.applyEligible === false) {
    return { kind: 'closed', label: 'Not taking applications', detail: null, tone: 'none', availableOn: null };
  }
  const available = parseIsoDate(unit.availableFrom);
  const availableOn = available && daysBetween(today, available) > 0 ? startOfDay(available) : null;

  if (moveIn) {
    if (!availableOn || availableOn <= startOfDay(moveIn)) {
      return { kind: 'ready', label: `Ready for ${formatShortDate(moveIn, today)}`, detail: null, tone: 'ok', availableOn };
    }
    return {
      kind: 'later',
      label: formatShortDate(availableOn, today),
      detail: `${describeGap(moveIn, availableOn)} later`,
      tone: 'later',
      availableOn,
    };
  }
  if (!availableOn) return { kind: 'ready', label: 'Ready now', detail: null, tone: 'ok', availableOn };
  return { kind: 'later', label: `Available ${formatShortDate(availableOn, today)}`, detail: null, tone: 'later', availableOn };
}

/** The decide sheet's line: "Ready Oct 28, a few days before you". */
export function describeMoveInFit(
  unit: Pick<PublicListingUnit, 'availableFrom' | 'applyEligible'>,
  moveIn: Date,
  today: Date,
): { tone: 'ok' | 'later' | 'none'; text: string } {
  const fit = dateFit(unit, moveIn, today);
  if (fit.kind === 'closed') return { tone: 'none', text: 'Not taking applications right now' };
  if (fit.kind === 'later' && fit.availableOn) {
    return { tone: 'later', text: `Available ${formatShortDate(fit.availableOn, today)}, ${describeGap(moveIn, fit.availableOn)} after your date` };
  }
  if (!fit.availableOn) return { tone: 'ok', text: 'Ready now' };
  const lead = daysBetween(fit.availableOn, moveIn);
  const when = formatShortDate(fit.availableOn, today);
  if (lead === 0) return { tone: 'ok', text: `Ready ${when}, the day you want` };
  if (lead === 1) return { tone: 'ok', text: `Ready ${when}, a day before you` };
  if (lead < 7) return { tone: 'ok', text: `Ready ${when}, a few days before you` };
  return { tone: 'ok', text: `Ready ${when}, ${describeGap(fit.availableOn, moveIn)} before you` };
}

const FIT_RANK: Record<DateFitKind, number> = { ready: 0, later: 1, closed: 2 };

/** Ready homes first, then the rest by when they free up, then by rent; closed homes last. */
export function sortByFit<T extends PublicListingUnit>(units: readonly T[], moveIn: Date | null, today: Date): T[] {
  return units
    .map((unit, index) => ({ unit, index, fit: dateFit(unit, moveIn, today) }))
    .sort((a, b) =>
      FIT_RANK[a.fit.kind] - FIT_RANK[b.fit.kind]
      || (a.fit.availableOn?.getTime() ?? 0) - (b.fit.availableOn?.getTime() ?? 0)
      || (a.unit.rent ?? Number.POSITIVE_INFINITY) - (b.unit.rent ?? Number.POSITIVE_INFINITY)
      || a.index - b.index)
    .map((entry) => entry.unit);
}

// ---------- the sentence ----------

export type BedsChoice = 'studio' | '1' | '2' | '3' | '4';

export const BEDS_CHOICES: ReadonlyArray<{ value: BedsChoice | null; label: string }> = [
  { value: 'studio', label: 'Studio' },
  { value: '1', label: '1+' },
  { value: '2', label: '2+' },
  { value: '3', label: '3+' },
  { value: '4', label: '4+' },
  { value: null, label: 'Any' },
];

export interface ListingSearch {
  beds: BedsChoice | null;
  rentMin: number | null;
  rentMax: number | null;
  moveIn: string | null;
  /** Free text kept from shared links (`q`); the sentence has no search box. */
  search: string;
}

function parseRent(value: string | null): number | null {
  const rent = Number(value);
  return value && Number.isFinite(rent) && rent > 0 ? Math.round(rent) : null;
}

export function parseListingSearch(params: URLSearchParams): ListingSearch {
  const beds = params.get('beds');
  const moveIn = params.get('moveIn');
  let rentMin = parseRent(params.get('rentMin'));
  let rentMax = parseRent(params.get('rentMax'));
  if (rentMin !== null && rentMax !== null && rentMin > rentMax) [rentMin, rentMax] = [rentMax, rentMin];
  return {
    beds: beds === 'studio' || beds === '0' ? 'studio' : beds === '1' || beds === '2' || beds === '3' || beds === '4' ? beds : null,
    rentMin,
    rentMax,
    moveIn: moveIn && /^\d{4}-\d{2}-\d{2}$/.test(moveIn) && parseIsoDate(moveIn) ? moveIn : null,
    search: params.get('q')?.trim().slice(0, 120) ?? '',
  };
}

export function writeListingSearch(previous: URLSearchParams, next: Partial<ListingSearch>): URLSearchParams {
  const params = new URLSearchParams(previous);
  const set = (key: string, value: string | null) => (value ? params.set(key, value) : params.delete(key));
  if ('beds' in next) set('beds', next.beds ?? null);
  if ('rentMin' in next) set('rentMin', next.rentMin ? String(next.rentMin) : null);
  if ('rentMax' in next) set('rentMax', next.rentMax ? String(next.rentMax) : null);
  if ('moveIn' in next) set('moveIn', next.moveIn ?? null);
  if ('search' in next) set('q', next.search?.trim() || null);
  return params;
}

export function hasListingFilters(search: ListingSearch): boolean {
  return Boolean(search.beds || search.rentMin || search.rentMax || search.moveIn || search.search);
}

export function bedsTokenLabel(beds: BedsChoice | null): string {
  if (beds === null) return 'any size';
  if (beds === 'studio') return 'a studio';
  return `${beds}+ bedrooms`;
}

export function matchesBeds(unit: PublicListingUnit, beds: BedsChoice | null): boolean {
  if (beds === null) return true;
  if (typeof unit.bedrooms !== 'number') return false;
  return beds === 'studio' ? unit.bedrooms === 0 : unit.bedrooms >= Number(beds);
}

export type RentRange = Pick<ListingSearch, 'rentMin' | 'rentMax'>;

export function matchesRent(unit: PublicListingUnit, range: RentRange): boolean {
  if (range.rentMin === null && range.rentMax === null) return true;
  if (typeof unit.rent !== 'number') return false;
  return (range.rentMin === null || unit.rent >= range.rentMin) && (range.rentMax === null || unit.rent <= range.rentMax);
}

export function filterUnits<T extends PublicListingUnit>(units: readonly T[], search: Pick<ListingSearch, 'beds' | 'rentMin' | 'rentMax'>): T[] {
  return units.filter((unit) => matchesBeds(unit, search.beds) && matchesRent(unit, search));
}

export interface PriceBounds {
  min: number;
  max: number;
  step: number;
}

/**
 * The price slider's ends: the cheapest and dearest published rent, rounded out to a step chosen
 * by the spread. Fewer than two distinct rents leave nothing to choose, so there is no slider.
 */
export function priceBounds(rents: readonly (number | null | undefined)[]): PriceBounds | null {
  const values = Array.from(new Set(rents.filter((rent): rent is number => typeof rent === 'number' && Number.isFinite(rent) && rent > 0)));
  if (values.length < 2) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min;
  const step = range > 4000 ? 500 : range > 1200 ? 250 : range > 400 ? 100 : 50;
  return { min: Math.floor(min / step) * step, max: Math.ceil(max / step) * step, step };
}

const clamp = (value: number, bounds: PriceBounds) => Math.min(bounds.max, Math.max(bounds.min, value));

/** The thumbs for a range: an open side sits at its end; a URL value outside the bounds is clamped for display only. */
export function priceSliderValue(range: RentRange, bounds: PriceBounds): [number, number] {
  return [clamp(range.rentMin ?? bounds.min, bounds), clamp(range.rentMax ?? bounds.max, bounds)];
}

/**
 * The thumbs back to a range. A thumb at its end means no limit on that side, never a number, so
 * every home stays reachable. A thumb the renter did not move keeps the range's own value.
 */
export function rangeFromPriceSlider(value: readonly number[], bounds: PriceBounds, previous: RentRange): RentRange {
  const [low, high] = [Math.min(value[0], value[1]), Math.max(value[0], value[1])];
  const [shownLow, shownHigh] = priceSliderValue(previous, bounds);
  return {
    rentMin: low === shownLow ? previous.rentMin : low <= bounds.min ? null : low,
    rentMax: high === shownHigh ? previous.rentMax : high >= bounds.max ? null : high,
  };
}

/**
 * The price clause reads as English with every range: "under [any price]", "under [$2,600]",
 * "from [$1,800]", "from [$1,800 to $2,600]". Words, not a dash: Satoshi's en dash is nearly an em wide.
 */
export function priceClause(range: RentRange): { connective: 'under' | 'from'; label: string } {
  const low = range.rentMin === null ? null : formatRent(range.rentMin);
  const high = range.rentMax === null ? null : formatRent(range.rentMax);
  if (low && high) return { connective: 'from', label: low === high ? low : `${low} to ${high}` };
  if (low) return { connective: 'from', label: low };
  return { connective: 'under', label: high ?? 'any price' };
}

/** The price card's readout: "$1,800 to $2,600", "Under $2,600", "From $1,800", "Any price". */
export function priceRangeLabel(range: RentRange): string {
  const { connective, label } = priceClause(range);
  if (range.rentMin !== null && range.rentMax !== null) return label;
  if (connective === 'from') return `From ${label}`;
  return range.rentMax === null ? 'Any price' : `Under ${label}`;
}

export function homesCountLabel(count: number): string {
  if (count === 0) return 'No homes';
  return count === 1 ? '1 home' : `${count} homes`;
}

// ---------- cards ----------

/** Today's title logic: unit and property, without repeating a name that is the same. */
export function unitTitle(unit: Pick<PublicListingUnit, 'unitName' | 'propertyName'>): string {
  const unitName = unit.unitName?.trim();
  const propertyName = unit.propertyName?.trim();
  if (unitName && propertyName && unitName.toLowerCase() !== propertyName.toLowerCase()) return `${unitName} · ${propertyName}`;
  return unitName || propertyName || 'Home';
}

export function formatBedsShort(value: number | null | undefined): string | null {
  if (typeof value !== 'number') return null;
  return value === 0 ? 'Studio' : `${value} bed`;
}

export function formatBathsShort(value: number | null | undefined): string | null {
  if (typeof value !== 'number') return null;
  return `${value.toLocaleString('en-US', { maximumFractionDigits: 1 })} bath`;
}

export function formatArea(value: number | null | undefined): string | null {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return null;
  return `${value.toLocaleString('en-US')} sq ft`;
}

/** "2 bed · 1 bath · 860 sq ft · Austin". */
export function unitFacts(unit: PublicListingUnit, options: { place?: boolean } = {}): string {
  return [
    formatBedsShort(unit.bedrooms),
    formatBathsShort(unit.bathrooms),
    formatArea(unit.areaSqft),
    options.place === false ? null : unit.city?.trim() || null,
  ].filter(Boolean).join(' · ');
}

export function unitAddress(unit: PublicListingUnit): string {
  const street = [unit.address1, unit.address2].filter((part) => part?.trim()).join(' ');
  const region = [unit.state, unit.postalCode].filter((part) => part?.trim()).join(' ');
  return [street, unit.city, region].filter((part) => part?.trim()).join(', ');
}

export function unitPhotos(unit: Pick<PublicListingUnit, 'photoUrls' | 'coverImageUrl'>): string[] {
  const photos = (unit.photoUrls ?? []).filter((url) => typeof url === 'string' && url.trim());
  if (unit.coverImageUrl && !photos.includes(unit.coverImageUrl)) photos.unshift(unit.coverImageUrl);
  return photos;
}

/** A maps search for the address; the data has no coordinates. */
export function mapsSearchUrl(unit: PublicListingUnit): string | null {
  const query = unitAddress(unit) || [unit.city, unit.state].filter(Boolean).join(', ');
  return query ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}` : null;
}
